import { useEffect, useState } from 'react';
import { MAX_PREVIEW_PERC } from '@components/Waveform/constants';
import { TimeSlotSelectorDocument, useQuery } from '@graphql/reactQuery';
import { useLatestRef } from '@hooks/useLatestRef';
import { useStableCallback } from '@hooks/useStableCallback';
import { loadTrack as load, seek, useAudioController } from '@store/audio/AudioController';
import { fadeOut, pause, play, setVolume } from '@store/audio/AudioEngineHTML5';
import { useAudioPosition } from '@store/audio/AudioPosition';

export function usePlaybackRange({ totalSlots, trackId }: { totalSlots: number; trackId: string }) {
  const { data } = useQuery(TimeSlotSelectorDocument, { variables: { id: trackId }, staleTime: 0 });
  const { duration } = data?.data.track ?? {};

  const { ready, playing, activeTrackId } = useAudioController();

  const { percentComplete } = useAudioPosition();
  const isActiveTrack = trackId === activeTrackId;
  const [playbackRange, setPlaybackRange] = useState<
    { startPerc: number; endPerc: number } | undefined
  >();
  const rangeRef = useLatestRef(playbackRange);
  const playingRef = useLatestRef(playing);

  const handleSlot = useStableCallback(async (slot: number) => {
    if (!isActiveTrack) {
      await load({ trackId });
    }
    await setVolume(1);

    const maxBars = totalSlots;
    const startBar = slot === 0 ? 0 : slot;

    const startPerc = startBar / maxBars;
    const endPerc = Math.min((startBar + 1) / maxBars, MAX_PREVIEW_PERC);

    setPlaybackRange({ startPerc, endPerc });
  });

  // apply seek after load
  useEffect(() => {
    if (isActiveTrack && playbackRange && ready && duration) {
      const { startPerc } = playbackRange;
      seek(startPerc * duration);
      if (!playingRef.current) play();
    }
  }, [isActiveTrack, ready, playbackRange, duration, playingRef]);

  useEffect(() => {
    const { endPerc } = rangeRef.current ?? {};
    const currPerc = percentComplete / 100;

    if (endPerc && currPerc >= endPerc && activeTrackId) {
      if (currPerc >= MAX_PREVIEW_PERC) {
        pause();
      } else {
        fadeOut(2.5);
        setPlaybackRange(undefined);
      }
    }
  }, [percentComplete, activeTrackId, rangeRef]);

  return { handleSlot };
}
